import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  PlayCircle,
  Heart,
  Users,
  Calendar,
  Compass,
  BookOpen,
  Church,
  MapPin,
  Music,
  Baby,
  Shield,
  Globe,
  Images,
} from "lucide-react";
import api from "@/lib/api";
import { LOGO_URL, APOSTLE_IMAGE, IMAGES } from "@/constants/branding";
import { Section, DividerGold } from "@/components/layout/Section";
import { PrayingHands } from "@/components/icons/PrayingHands";
import { useLiveRefetch } from "@/hooks/useLiveRefetch";

const iconLibrary = { Users, Music, Baby, Heart, Shield, Globe, Compass, BookOpen, Church };

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7 },
};

export default function Home() {
  const [homepage, setHomepage] = useState(null);
  const [sermons, setSermons] = useState([]);
  const [events, setEvents] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [devotional, setDevotional] = useState(null);
  const [gallery, setGallery] = useState([]);

  const load = useCallback(() => {
    Promise.all([
      api.get("/homepage"),
      api.get("/sermons", { params: { limit: 6 } }),
      api.get("/youtube/videos", { params: { limit: 6 } }).catch(() => ({ data: { videos: [] } })),
      api.get("/events", { params: { upcoming_only: true } }),
      api.get("/ministries"),
      api.get("/testimonies", { params: { limit: 3 } }),
      api.get("/devotionals/today"),
      api.get("/gallery"),
    ]).then(([h, s, y, e, m, t, d, g]) => {
      setHomepage(h.data);
      const admin = s.data || [];
      const yt = (y.data?.videos || []).map((v) => ({
        id: `yt-${v.youtube_id}`,
        title: v.title,
        preacher: "CLBC",
        date: v.published,
        scripture: null,
        youtube_id: v.youtube_id,
        thumbnail: v.thumbnail,
        series: "CLBC YouTube",
      }));
      const seen = new Set(admin.map((x) => x.youtube_id).filter(Boolean));
      const merged = [...admin, ...yt.filter((v) => !seen.has(v.youtube_id))].sort(
        (a, b) => (a.date < b.date ? 1 : -1)
      );
      setSermons(merged);
      setEvents(e.data);
      setMinistries(m.data);
      setTestimonies(t.data);
      setDevotional(d.data);
      setGallery(g.data || []);
    });
  }, []);

  useEffect(() => { load(); }, [load]);
  useLiveRefetch(load, 30000);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden radial-bg">
        <div className="absolute -top-40 -right-24 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-gold/25 to-transparent blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-royal/10 blur-3xl" />

        <div className="container-x pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-4 mb-8"
            >
              <img
                src={LOGO_URL}
                alt="CLBC logo"
                className="h-20 w-20 rounded-full ring-4 ring-gold/30 shadow-lg"
              />
              <div>
                <div className="kicker">Welcome Home</div>
                <div className="mt-2 font-serif text-xl text-royal">Christ Love Breed Church</div>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-royal-800"
            >
              Where God's <span className="gold-text">Love</span> gives
              <br /> birth to a new you.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              A Spirit-filled family under the ministry of{" "}
              <span className="text-royal font-semibold">Apostle Percy Konotey</span> —
              raising disciples, demonstrating God's love, and transforming lives through the Gospel of Jesus Christ.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <Link to="/new-here" data-testid="hero-cta-visit" className="btn-primary">
                Plan a Visit <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/watch-live" data-testid="hero-cta-watch" className="btn-secondary">
                <PlayCircle className="w-5 h-5" /> Watch Live
              </Link>
              <Link to="/give" data-testid="hero-cta-give" className="btn-ghost">
                <Heart className="w-4 h-4" /> Give Online
              </Link>
              <Link to="/prayer" data-testid="hero-cta-prayer" className="btn-ghost">
                <PrayingHands className="w-4 h-4" /> Prayer Request
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="mt-14 flex flex-wrap gap-8 text-sm text-slate-500"
            >
              <div>
                <div className="text-3xl font-serif text-royal">2</div>
                <div className="uppercase tracking-widest text-xs mt-1">Weekly Services</div>
              </div>
              <div className="w-px bg-slate-200" />
              <div>
                <div className="text-3xl font-serif text-royal">{ministries.length || 2}</div>
                <div className="uppercase tracking-widest text-xs mt-1">Active Ministries</div>
              </div>
              <div className="w-px bg-slate-200" />
              <div>
                <div className="text-3xl font-serif text-royal">1</div>
                <div className="uppercase tracking-widest text-xs mt-1">Family in Christ</div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-6 relative"
          >
            <div className="absolute -inset-6 bg-gradient-to-br from-gold/30 via-transparent to-royal/20 blur-3xl -z-10" />
            <div className="relative aspect-[4/5] max-w-md lg:max-w-lg mx-auto rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white">
              <img
                src={APOSTLE_IMAGE}
                alt="Apostle Percy Konotey"
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-royal-800/85 via-royal-800/40 to-transparent p-8 text-white">
                <div className="text-xs tracking-[0.3em] uppercase text-gold">General Overseer</div>
                <div className="font-serif text-3xl mt-2">Apostle Percy Konotey</div>
              </div>
            </div>
            <div className="hidden lg:block absolute -bottom-6 -right-6 glass rounded-2xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gold/15 flex items-center justify-center text-gold-500">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Next Service</div>
                  <div className="font-semibold text-royal">Sunday · 12:00 noon</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT OVERVIEW */}
      <Section testId="home-about">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-xl">
              <img src={IMAGES.worship} alt="Worship at CLBC" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -top-8 -right-8 w-40 h-40 rounded-full bg-gold/20 blur-2xl" />
          </div>
          <div className="lg:col-span-7">
            <div className="kicker mb-6">About CLBC</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-royal-800 leading-tight tracking-tight">
              A house where heaven meets earth — and lives are forever changed.
            </h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">
              {homepage?.welcome ||
                "Christ Love Breed Church is a Spirit-filled family committed to raising disciples of Christ, demonstrating God's love, and transforming lives through the Gospel."}
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              <div className="card-soft p-6">
                <div className="w-11 h-11 rounded-full bg-royal/10 text-royal flex items-center justify-center">
                  <Compass className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-serif text-xl text-royal">Our Vision</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  {homepage?.vision || "To raise a generation of Christ-loving disciples carrying God's presence into every sphere."}
                </p>
              </div>
              <div className="card-soft p-6">
                <div className="w-11 h-11 rounded-full bg-gold/15 text-gold-500 flex items-center justify-center">
                  <Heart className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-serif text-xl text-royal">Our Mission</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  {homepage?.mission || "To preach Christ, teach the Word, demonstrate His love, and equip believers for their calling."}
                </p>
              </div>
            </div>

            <div className="mt-10 flex gap-3">
              <Link to="/about" className="btn-secondary">Read Our Story</Link>
              <Link to="/overseer" className="btn-ghost">Meet the Overseer <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </Section>

      {/* UPCOMING EVENTS */}
      <Section className="bg-slate-50/70 rounded-t-[3rem]" testId="home-events">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div>
            <div className="kicker mb-4">Upcoming</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-royal-800 tracking-tight">
              Come and encounter God
            </h2>
          </div>
          <Link to="/events" className="btn-ghost">All Events <ArrowRight className="w-4 h-4" /></Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.slice(0, 3).map((ev, i) => {
            const d = new Date(ev.starts_at);
            return (
              <motion.article
                key={ev.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="card-soft p-8"
                data-testid={`home-event-${i}`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-center bg-royal text-white rounded-2xl px-4 py-3 shadow-md">
                    <div className="text-[10px] uppercase tracking-widest text-gold">
                      {d.toLocaleString("en", { month: "short" })}
                    </div>
                    <div className="font-serif text-3xl leading-none">{d.getDate()}</div>
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">
                    {d.toLocaleString("en", { weekday: "long" })} · {d.toLocaleString("en", { hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
                <h3 className="font-serif text-2xl text-royal mt-6 leading-snug">{ev.title}</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed line-clamp-3">{ev.description}</p>
                {ev.location && (
                  <div className="mt-5 flex items-center gap-2 text-xs text-slate-500">
                    <MapPin className="w-3.5 h-3.5" /> {ev.location}
                  </div>
                )}
              </motion.article>
            );
          })}
          {events.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-10">More events coming soon.</div>
          )}
        </div>
      </Section>

      {/* LATEST SERMONS */}
      <Section testId="home-sermons">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div>
            <div className="kicker mb-4">Latest Sermons</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-royal-800 tracking-tight">
              Be built up in the Word
            </h2>
          </div>
          <Link to="/sermons" className="btn-ghost">All Sermons <ArrowRight className="w-4 h-4" /></Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sermons.slice(0, 3).map((s, i) => (
            <motion.article
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              key={s.id}
              className="card-soft overflow-hidden"
              data-testid={`home-sermon-${i}`}
            >
              <div className="aspect-video bg-royal-800 relative overflow-hidden">
                {s.youtube_id ? (
                  <img
                    src={`https://img.youtube.com/vi/${s.youtube_id}/hqdefault.jpg`}
                    alt={s.title}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-transform duration-500"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <img src={IMAGES.bible} alt="" className="w-full h-full object-cover opacity-80" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-royal-800/60 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white">
                  <div className="w-10 h-10 rounded-full bg-gold text-royal-800 flex items-center justify-center">
                    <PlayCircle className="w-5 h-5" />
                  </div>
                  <span className="text-xs uppercase tracking-widest">Watch</span>
                </div>
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-gold-500">{s.series || "Sermon"}</div>
                <h3 className="mt-2 font-serif text-xl text-royal leading-snug">{s.title}</h3>
                {s.scripture && <p className="mt-2 scripture text-sm">"{s.scripture}"</p>}
                <div className="mt-4 text-xs text-slate-500 flex justify-between">
                  <span>{s.preacher}</span>
                  <span>{new Date(s.date).toLocaleDateString()}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </Section>

      {/* MINISTRIES */}
      <Section className="bg-royal-800 text-white rounded-t-[3rem]" testId="home-ministries">
        <div className="max-w-2xl">
          <div className="kicker mb-4 text-gold">Ministries</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">
            Find your place. Serve with purpose.
          </h2>
          <p className="mt-5 text-white/70 text-lg">
            Every believer has a divine assignment. Discover the ministry that matches your gifts and calling.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ministries.slice(0, 6).map((m, i) => {
            const Icon = iconLibrary[m.icon] || Users;
            return (
              <motion.div
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                key={m.id}
                className="group relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 hover:border-gold/60 hover:bg-white/10 transition-all"
                data-testid={`home-ministry-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-gold/15 text-gold flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-serif text-2xl">{m.name}</h3>
                <p className="mt-1 text-xs uppercase tracking-widest text-gold/80">{m.tagline}</p>
                <p className="mt-4 text-white/70 text-sm leading-relaxed">{m.description}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12">
          <Link to="/ministries" className="inline-flex items-center gap-2 text-gold hover:underline">
            Explore all ministries <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      {/* SERVICE TIMES */}
      <Section testId="home-service-times">
        <div className="text-center max-w-2xl mx-auto">
          <div className="kicker justify-center mb-4">Gather with us</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-royal-800 tracking-tight">Service Times</h2>
          <DividerGold className="mt-6" />
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {(homepage?.service_times || []).map((st, i) => (
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              key={i}
              className="card-soft p-8 text-center"
            >
              <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-gold to-gold-500 text-royal-800 flex items-center justify-center shadow-md">
                <Church className="w-6 h-6" />
              </div>
              <h3 className="mt-6 font-serif text-2xl text-royal">{st.name}</h3>
              <p className="mt-3 text-slate-600">{st.time}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* TESTIMONIES */}
      <Section className="bg-slate-50/70 rounded-t-[3rem]" testId="home-testimonies">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <div className="kicker mb-4">Testimonies</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-royal-800 tracking-tight">
              Real stories.
              <br /> Real encounters.
            </h2>
            <p className="mt-5 text-slate-600">
              God is doing wonders in the lives of His people. Read what He's done — and share what He's done for you.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/testimonies" className="btn-secondary">Read More</Link>
              <Link to="/testimonies#share" className="btn-ghost">Share yours <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
            {testimonies.map((t, i) => (
              <motion.blockquote
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                key={t.id}
                className="card-soft p-8"
                data-testid={`home-testimony-${i}`}
              >
                <div className="text-gold text-4xl font-serif leading-none">"</div>
                <p className="mt-2 text-slate-700 leading-relaxed">{t.content}</p>
                <footer className="mt-6 text-sm">
                  <div className="font-semibold text-royal">{t.name}</div>
                  {t.location && <div className="text-slate-500 text-xs">{t.location}</div>}
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </Section>

      {/* GALLERY PREVIEW */}
      <Section testId="home-gallery">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div>
            <div className="kicker mb-4">Moments</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-royal-800 tracking-tight">
              Life at CLBC
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl">
              A glimpse of worship, fellowship and Kingdom moments in our house.
            </p>
          </div>
          <Link to="/gallery" className="btn-ghost">
            <Images className="w-4 h-4" /> View Full Gallery <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {gallery.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] gap-3">
            {gallery.slice(0, 8).map((g, i) => {
              const spans = [
                "row-span-2 col-span-2",
                "",
                "",
                "row-span-2",
                "",
                "col-span-2",
                "",
                "",
              ];
              return (
                <motion.figure
                  key={g.id}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                  className={`group relative overflow-hidden rounded-2xl shadow-sm ring-1 ring-slate-200/60 ${spans[i] || ""}`}
                  data-testid={`home-gallery-${i}`}
                >
                  <Link to="/gallery" className="block w-full h-full">
                    <img
                      src={g.image}
                      alt={g.title || "CLBC moment"}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-royal-800/70 via-royal-800/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    {g.title && (
                      <figcaption className="absolute bottom-0 left-0 right-0 p-4 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        {g.title}
                      </figcaption>
                    )}
                  </Link>
                </motion.figure>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-slate-500 py-10">Photos coming soon.</div>
        )}
      </Section>

      {/* DEVOTIONAL PREVIEW */}
      {devotional && (
        <Section testId="home-devotional">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-5">
              <div className="aspect-square rounded-[2.5rem] overflow-hidden shadow-xl">
                <img src={IMAGES.bible} alt="Bible" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="kicker mb-4">Today's Devotional</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-royal-800 tracking-tight leading-tight">
                {devotional.title}
              </h2>
              <p className="mt-4 scripture text-lg">"{devotional.scripture}"</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-gold-500">{devotional.scripture_ref}</p>
              <p className="mt-8 text-slate-600 whitespace-pre-line leading-relaxed">
                {devotional.body?.split("\n\n")[0]}
              </p>
              <div className="mt-8">
                <Link to="/devotional" className="btn-secondary">Read Full Devotional <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* SALVATION CALL */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.hands} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-royal-800/85" />
        </div>
        <div className="container-x text-center text-white max-w-3xl mx-auto">
          <div className="kicker justify-center text-gold mb-6">Your Invitation</div>
          <h2 className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05]">
            Jesus is calling you home.
          </h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed">
            {homepage?.salvation_call ||
              "If you're ready to give your life to Christ, or you simply need someone to pray with you — we would love to walk with you."}
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/new-here" className="btn-primary">I want to know Jesus</Link>
            <Link to="/prayer" className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-royal">
              Request Prayer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
